import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  fetchInvoiceSearchApi,
  InvoiceSearchParams,
  InvoiceSearchResponse,
  InvoiceSearchItem,
} from '../../api/invoice-search';

export const fetchInvoiceSearch = createAsyncThunk(
  'invoiceSearch/fetchInvoiceSearch',
  async (payload: InvoiceSearchParams, thunkAPI) => {
    try {
      const data = await fetchInvoiceSearchApi(payload);
      return data;
    } catch (error: any) {
      const message =
        (error?.response?.data?.message) ||
        error?.message ||
        'Failed to fetch invoices';
      return thunkAPI.rejectWithValue(message);
    }
  }
);


type InvoiceSearchState = {
  searchResults: InvoiceSearchItem[] | null;
  loading: boolean;
  error: string | null;
  message: string | null;
  pagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  filters: {
    searchQuery: string;
  };
};

const initialState: InvoiceSearchState = {
  searchResults: null,
  loading: false,
  error: null,
  message: null,
  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  },
  filters: {
    searchQuery: '',
  },
};

const invoiceSearchSlice = createSlice({
  name: 'invoiceSearch',
  initialState,
  reducers: {
    clearSearchResults: (state) => {
      state.searchResults = null;
      state.error = null;
      state.message = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearMessage: (state) => {
      state.message = null;
    },
    setCurrentPage: (state, action) => {
      state.pagination.currentPage = action.payload;
    },
    setPageSize: (state, action) => {
      state.pagination.pageSize = action.payload;
      state.pagination.currentPage = 1;
    },
    setSearchQuery: (state, action) => {
      state.filters.searchQuery = action.payload;
      state.pagination.currentPage = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInvoiceSearch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoiceSearch.fulfilled, (state, action) => {
        const totalPages = Math.ceil(action.payload.totalCount / state.pagination.pageSize);
        state.loading = false;
        state.searchResults = action.payload.response;
        state.pagination.totalCount = action.payload.totalCount;
        state.pagination.totalPages = totalPages;
        state.pagination.hasNextPage =
        state.pagination.currentPage < state.pagination.totalPages;
        state.pagination.hasPreviousPage = state.pagination.currentPage > 1;
        state.message = action.payload.message;
      })
      .addCase(fetchInvoiceSearch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.searchResults = null;
      });
  },
});

export const {
  clearSearchResults,
  clearError,
  clearMessage,
  setCurrentPage,
  setPageSize,
  setSearchQuery,
} = invoiceSearchSlice.actions;

export default invoiceSearchSlice.reducer;
